import { Card, Rating, ScheduleData, ScheduleResult } from '../types/models';

/**
 * SpacedRepetitionService implements the SM-2 (SuperMemo 2) algorithm
 * for calculating optimal review intervals based on user performance.
 * 
 * The SM-2 algorithm adjusts the interval between reviews based on:
 * - The user's rating of how well they remembered the card
 * - The card's current ease factor (difficulty)
 * - The number of successful repetitions
 * 
 * Algorithm details:
 * - Rating 'again': Reset to beginning (interval = 1 day, repetitions = 0)
 * - Rating 'hard': Multiply interval by 1.2, reduce ease factor
 * - Rating 'good': Use standard SM-2 calculation
 * - Rating 'easy': Use SM-2 calculation with bonus multiplier
 */
export class SpacedRepetitionService {
  // SM-2 algorithm constants
  private static readonly MIN_EASE_FACTOR = 1.3;
  private static readonly DEFAULT_EASE_FACTOR = 2.5;
  private static readonly INITIAL_INTERVAL = 1; // days
  private static readonly SECOND_INTERVAL = 6; // days
  private static readonly MAX_INTERVAL = 180; // days (6 months cap)

  /**
   * Calculate the next review schedule for a card based on user rating
   * 
   * @param card - The card being reviewed
   * @param rating - User's performance rating ('again', 'hard', 'good', 'easy')
   * @param reviewDate - The date of the review (defaults to now)
   * @returns ScheduleResult with next review date, interval, ease factor, and repetitions
   */
  calculateNextReview(
    card: Card,
    rating: Rating,
    reviewDate: Date = new Date()
  ): ScheduleResult {
    const currentSchedule = card.schedule;
    let interval: number;
    let easeFactor: number;
    let repetitions: number;

    switch (rating) {
      case 'again':
        // Failed recall - re-queue immediately (interval = 0)
        interval = 0;
        repetitions = 0;
        easeFactor = Math.max(
          currentSchedule.easeFactor - 0.2,
          SpacedRepetitionService.MIN_EASE_FACTOR
        );
        break;

      case 'tomorrow':
        // Defer exactly 1 day, no change to ease factor or repetitions
        interval = 1;
        repetitions = currentSchedule.repetitions;
        easeFactor = currentSchedule.easeFactor;
        break;

      case 'hard':
        // Difficult recall - increase interval slightly, reduce ease factor
        interval = Math.max(
          currentSchedule.interval * 1.2,
          SpacedRepetitionService.INITIAL_INTERVAL
        );
        interval = Math.min(interval, SpacedRepetitionService.MAX_INTERVAL);
        repetitions = currentSchedule.repetitions; // Don't increment
        easeFactor = Math.max(
          currentSchedule.easeFactor - 0.15,
          SpacedRepetitionService.MIN_EASE_FACTOR
        );
        break;

      case 'good':
        // Normal recall - standard SM-2 calculation
        repetitions = currentSchedule.repetitions + 1;
        easeFactor = currentSchedule.easeFactor;

        if (repetitions === 1) {
          interval = SpacedRepetitionService.INITIAL_INTERVAL;
        } else if (repetitions === 2) {
          interval = SpacedRepetitionService.SECOND_INTERVAL;
        } else {
          interval = Math.round(currentSchedule.interval * easeFactor);
        }
        interval = Math.min(interval, SpacedRepetitionService.MAX_INTERVAL);
        break;

      case 'easy':
        // Easy recall - SM-2 with bonus multiplier and ease factor increase
        repetitions = currentSchedule.repetitions + 1;
        easeFactor = currentSchedule.easeFactor + 0.15;

        if (repetitions === 1) {
          interval = SpacedRepetitionService.SECOND_INTERVAL;
        } else if (repetitions === 2) {
          interval = SpacedRepetitionService.SECOND_INTERVAL * 2;
        } else {
          interval = Math.round(currentSchedule.interval * easeFactor * 1.3);
        }
        interval = Math.min(interval, SpacedRepetitionService.MAX_INTERVAL);
        break;
    }

    // Calculate next review date (interval=0 means immediately)
    const nextReviewDate = new Date(reviewDate);
    if (interval > 0) {
      nextReviewDate.setDate(nextReviewDate.getDate() + interval);
    }

    return {
      nextReviewDate,
      interval,
      easeFactor,
      repetitions,
    };
  }

  /**
   * Preview what interval each rating would produce for a card (for UI labels)
   */
  previewIntervals(card: Card): Record<Rating, number> {
    const ratings: Rating[] = ['again', 'tomorrow', 'hard', 'good', 'easy'];
    const result = {} as Record<Rating, number>;
    for (const rating of ratings) {
      result[rating] = this.calculateNextReview(card, rating).interval;
    }
    return result;
  }

  /**
   * Initialize schedule data for a new card
   * 
   * @param card - The card to initialize (only needs id, doesn't need existing schedule)
   * @returns ScheduleData with default values for a new card
   */
  initializeCardSchedule(card: Partial<Card>): ScheduleData {
    const now = new Date();
    
    return {
      nextReviewDate: now, // Available for review immediately
      interval: 0, // Not yet reviewed
      easeFactor: SpacedRepetitionService.DEFAULT_EASE_FACTOR,
      repetitions: 0,
      lastReviewDate: null,
    };
  }

  /**
   * Get cards that are due for review on or before the specified date
   * 
   * @param cards - Array of cards to filter
   * @param date - The date to check against (defaults to now)
   * @returns Array of cards that are due for review
   */
  getCardsDue(cards: Card[], date: Date = new Date()): Card[] {
    return cards.filter(card => {
      const nextReview = new Date(card.schedule.nextReviewDate);
      return nextReview <= date;
    });
  }
}
